import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useCreateCertificate } from "./useCreateCertificate";
import { useUpdateCertificate } from "./useUpdateCertificate";

import { ColorRing } from "react-loader-spinner";
import Modal from "../../ui/Modal";
import FileInputPreview from "../../ui/FileInputPreview";
import { getImageUrl } from "../../services/api";

const CertificateModal = ({ certificateToEdit, onClose }) => {
  const { createCertificate, isCreating } = useCreateCertificate();
  const { updateCertificate, isUpdating } = useUpdateCertificate();
  const isWorking = isCreating || isUpdating;

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      title: "",
      verificationUrl: "",
    },
  });

  useEffect(() => {
    if (certificateToEdit) {
      reset({
        title: certificateToEdit.title,
        verificationUrl: certificateToEdit.verificationUrl,
      });
    }
  }, [certificateToEdit, reset]);

  const onSubmit = (data) => {
    const formData = new FormData();
    formData.append("title", data.title);
    if (data.verificationUrl) formData.append("verificationUrl", data.verificationUrl);

    if (data.image && data.image[0]) {
      formData.append("image", data.image[0]);
    }
    if (data.pdf && data.pdf[0]) {
      formData.append("pdf", data.pdf[0]);
    }

    if (certificateToEdit) {
      updateCertificate(
        { id: certificateToEdit.id, data: formData },
        { onSuccess: onClose }
      );
    } else {
      createCertificate(formData, { onSuccess: onClose });
    }
  };

  return (
    <Modal
      isLoading={isWorking}
      onClose={onClose}
      title={certificateToEdit ? "Edit Certificate" : "Add New Certificate"}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 font-primary text-white">
        <div className="space-y-6">
          <div>
            <label className="mb-2 block text-sm font-medium text-stone-300">Certificate Title</label>
            <input
              type="text"
              className="h-12 w-full rounded-lg border-transparent bg-transparent p-4 shadow-xl outline-none placeholder:text-stone-400 focus:border-2 focus:border-primary"
              placeholder="e.g. React Native Developer"
              {...register("title", { required: "Title is required" })}
            />
            {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title.message}</p>}
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-stone-300">Verification URL</label>
            <input
              type="url"
              className="h-12 w-full rounded-lg border-transparent bg-transparent p-4 shadow-xl outline-none placeholder:text-stone-400 focus:border-2 focus:border-primary"
              placeholder="https://..."
              {...register("verificationUrl")}
            />
            {errors.verificationUrl && <p className="text-xs text-red-500 mt-1">{errors.verificationUrl.message}</p>}
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-stone-300">Image</label>
              <FileInputPreview
                registerProps={register("image", { required: !certificateToEdit && "Image is required" })}
                existingImageUrl={certificateToEdit?.image ? getImageUrl(certificateToEdit.image) : null}
                wrapperClassName="relative flex h-12 w-full items-center justify-center rounded-lg border-2 border-dashed border-stone-600 bg-black/20 hover:bg-black/30"
                inputClassName="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                previewClassName="absolute -top-2 -translate-y-full h-16 w-16 rounded-lg object-cover border border-stone-600"
              >
                <span className="text-xs text-stone-400">Click to upload image</span>
              </FileInputPreview>
              {errors.image && <p className="text-xs text-red-500 mt-1">{errors.image.message}</p>}
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-stone-300">PDF Document</label>
              <div className="relative flex h-12 w-full items-center justify-center rounded-lg border-2 border-dashed border-stone-600 bg-black/20 hover:bg-black/30">
                <input
                  type="file"
                  accept="application/pdf"
                  className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                  {...register("pdf")}
                />
                <span className="text-xs text-stone-400">Click to upload PDF</span>
              </div>
              {errors.pdf && <p className="text-xs text-red-500 mt-1">{errors.pdf.message}</p>}
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={isWorking}
            className="btn flex min-w-[8rem] items-center justify-center border-none bg-gradient-to-tr from-sky-600 to-cyan-400 py-3 font-semibold uppercase text-white shadow-lg hover:from-cyan-400 hover:to-sky-600 active:scale-95 disabled:opacity-50 transition-all rounded-lg"
          >
            {isWorking ? (
              <div className="flex items-center gap-2">
                <ColorRing
                  visible={true}
                  height="20"
                  width="20"
                  ariaLabel="blocks-loading"
                  colors={["#fff", "#fff", "#fff", "#fff", "#fff"]}
                />
                <span>Processing...</span>
              </div>
            ) : (
              certificateToEdit ? "Update Certificate" : "Create Certificate"
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CertificateModal;
